// Libs
import { ModelBase } from "@shoothill/core";
import * as MobX from "mobx";
import { observable } from "mobx";
import moment, { Moment } from "moment";
import { IsNotEmpty } from "class-validator";

// Model
import { ApplicantModel } from "Globals/Models/Domain/ApplicantModel";

// Dto
import { ApplicationFormDTO } from "../DTO/ApplicationFormDTO";
import { ApplicationFormAndRelatedDTO } from "../DTO/ApplicationFormAndRelatedDTO";
import { UpsertApplicationFormDTO } from "../DTO/Upsert/UpsertApplicationFormDTO";
import { EmploymentDetailsDTO } from "../DTO/EmploymentDetailsDTO";
import { PersonalDetailsDTO } from "../DTO/PersonalDetailsDTO";
import { PreviousAddressDTO } from "../DTO/PreviousAddressDTO";
import { FamilyAddressDTO } from "../DTO/FamilyAddressDTO";
import { ArmedForcesProofDTO } from "../DTO/ArmedForcesProofDTO";
import { AuditedAccountDTO } from "../DTO/AuditedAccountDTO";
import { IncomeDetailDTO } from "Globals/Models/DTO/IncomeDetailDTO";
import { MoneyGifterDTO } from "Globals/Models/DTO/MoneyGifterDTO";
import { OutstandingLoanDTO } from "Globals/Models/DTO/OutstandingLoanDTO";
import { CreditCardDTO } from "Globals/Models/DTO/CreditCardDTO";
import { FinancialCommitmentDTO } from "Globals/Models/DTO/FinancialCommitmentDTO";
import { StatementDetailDTO } from "../DTO/StatementDetailDTO";
import { PropertyDetailDTO } from "../DTO/PropertyDetailDTO";
import { PropertyDetailPersonDTO } from "../DTO/PropertyDetailPersonDTO";
import { CurrentPropertyDetailDTO } from "../DTO/CurrentPropertyDetailDTO";
import { NewPropertyDetailDTO } from "../DTO/NewPropertyDetailDTO";
import { BackgroundInformationDTO } from "../DTO/BackgroundInformationDTO";
import { DeclarationDTO } from "../DTO/DeclarationDTO";
import { CreditReportDTO } from "../DTO/CreditReportDTO";
import { SupportingInformationDTO } from "../DTO/SupportingInformationDTO";
import { Amendment } from "../Amendment";

export class ApplicationFormModel extends ModelBase<ApplicationFormModel, ApplicationFormAndRelatedDTO> {
    @MobX.observable public id: string | null = null;
    @MobX.observable public createdByUserId: string = "";
    @MobX.observable public statusId: string = "";
    @MobX.observable public lastSavedDate: Date | null = null;
    @MobX.observable public createdDate: Date | null = null;
    @MobX.observable public submittedDate: Date | null = null;
    @MobX.observable public isSubmitted: boolean = false;
    @MobX.observable public isCompleted: boolean = false;
    @MobX.observable public isDeleted: boolean = false;
    @MobX.observable public rejectReasonId: string | null = null;
    @MobX.observable public rejectReasonDetail: string | null = null;
    @MobX.observable public requestedSupportingInfo: boolean = false;
    @observable public applicantModels: ApplicantModel[] = [];

    @MobX.action
    public fromDto = (model: ApplicationFormAndRelatedDTO): void => {
        if (model !== null && model !== undefined) {
            this.id = model.applicationForm.id;
            this.createdByUserId = model.applicationForm.createdByUserId;
            this.statusId = model.applicationForm.statusId;
            this.lastSavedDate = model.applicationForm.lastSavedDate;
            this.createdDate = model.applicationForm.createdDate;
            this.submittedDate = model.applicationForm.submittedDate;
            this.isSubmitted = model.applicationForm.isSubmitted;
            this.isCompleted = model.applicationForm.isCompleted;
            this.rejectReasonId = model.applicationForm.rejectReasonId;
            this.isDeleted = model.applicationForm.isDeleted;
            this.rejectReasonDetail = model.applicationForm.rejectReasonDetail;
            this.requestedSupportingInfo = model.applicationForm.requestedSupportingInfo;

            // Load applicants
            model.applicants.forEach((applicant) => {
                let localApplicantModel: ApplicantModel = new ApplicantModel();

                // Start - Personal details
                let personalDetails: PersonalDetailsDTO | undefined = model.personalDetails.find((pd) => pd.applicantId == applicant.id);
                let personalDetailPreviousAddresses: PreviousAddressDTO[] | undefined;
                let personalDetailFamilyAddresses: FamilyAddressDTO[] | undefined;

                // Load personal details for each applicant
                if (personalDetails !== undefined) {
                    // Load previous addresses for each applicant
                    if (model.personalDetailPreviousAddresses !== undefined) {
                        personalDetailPreviousAddresses = model.personalDetailPreviousAddresses.filter(
                            (pda) => pda.personalDetailId == (personalDetails ? personalDetails.id : ""),
                        );
                    }
                    // Load family addresses for each applicant
                    if (model.personalDetailFamilyAddresses !== undefined) {
                        personalDetailFamilyAddresses = model.personalDetailFamilyAddresses.filter((pda) => pda.personalDetailId == (personalDetails ? personalDetails.id : ""));
                    }
                }

                // End - Personal details

                // Start - Employement details
                let employmentDetails: EmploymentDetailsDTO | undefined = model.employmentDetails.find((ed) => ed.applicantId == applicant.id);
                let employmentDetailsArmedForcesProof: ArmedForcesProofDTO[] | undefined;
                let employmentDetailsAuditedAccount: AuditedAccountDTO[] | undefined;
                if (employmentDetails !== undefined) {
                    // Load previous addresses for each applicant
                    if (model.employmentDetailsArmedForcesProof !== undefined) {
                        employmentDetailsArmedForcesProof = model.employmentDetailsArmedForcesProof.filter(
                            (a) => a.employmentDetailId == (employmentDetails ? employmentDetails.id : ""),
                        );
                    }
                    // Load audited accounts for each applicant
                    if (model.employmentDetailsAuditedAccount !== undefined) {
                        employmentDetailsAuditedAccount = model.employmentDetailsAuditedAccount.filter(
                            (a) => a.employmentDetailId == (employmentDetails ? employmentDetails.id : ""),
                        );
                    }
                }
                // End - Employement details

                // Start - Income details
                let incomeDetails: IncomeDetailDTO | undefined = model.incomeDetails.find((id) => id.applicantId == applicant.id);
                let incomeDetailsMoneyGifter: MoneyGifterDTO[] | undefined;
                let incomeDetailsOutstandingLoan: OutstandingLoanDTO[] | undefined;
                let incomeDetailsCreditCard: CreditCardDTO[] | undefined;
                let incomeDetailsFinancialCommitment: FinancialCommitmentDTO[] | undefined;

                if (incomeDetails !== undefined) {
                    if (model.incomeDetailsMoneyGifter !== undefined) {
                        incomeDetailsMoneyGifter = model.incomeDetailsMoneyGifter.filter((a) => a.incomeDetailId == (incomeDetails ? incomeDetails.id : ""));
                    }
                    if (model.incomeDetailsOutstandingLoan !== undefined) {
                        incomeDetailsOutstandingLoan = model.incomeDetailsOutstandingLoan.filter((a) => a.incomeDetailId == (incomeDetails ? incomeDetails.id : ""));
                    }
                    if (model.incomeDetailsCreditCard !== undefined) {
                        incomeDetailsCreditCard = model.incomeDetailsCreditCard.filter((a) => a.incomeDetailId == (incomeDetails ? incomeDetails.id : ""));
                    }
                    if (model.incomeDetailsFinancialCommitment !== undefined) {
                        incomeDetailsFinancialCommitment = model.incomeDetailsFinancialCommitment.filter((a) => a.incomeDetailId == (incomeDetails ? incomeDetails.id : ""));
                    }
                }
                // End - Income details

                // Start - Statement details
                let statementDetails: StatementDetailDTO | undefined = model.statementDetail.find((id) => id.applicantId == applicant.id);
                // End - Statement details

                // Start - Property details
                let propertyDetails: PropertyDetailDTO | undefined = model.propertyDetails.find((id) => id.applicantId == applicant.id);
                let propertyDetailsPerson: PropertyDetailPersonDTO[] | undefined;

                if (propertyDetails !== undefined) {
                    if (model.propertyDetailsPerson !== undefined) {
                        propertyDetailsPerson = model.propertyDetailsPerson.filter((a) => a.propertyDetailId == (propertyDetails ? propertyDetails.id : ""));
                    }
                }
                // End - Property details

                // Start - Current property details
                let currentPropertyDetails: CurrentPropertyDetailDTO | undefined = model.currentPropertyDetails.find((id) => id.applicantId == applicant.id);
                // End - Current property details

                // Start - New property details
                let newPropertyDetails: NewPropertyDetailDTO | undefined = model.newPropertyDetails.find((id) => id.applicantId == applicant.id);
                // End - New property details

                // Start - background information
                let backgroundInformation: BackgroundInformationDTO | undefined = model.backgroundInformation.find((id) => id.applicantId == applicant.id);
                // End - background information

                // Start - declaration information
                let declaration: DeclarationDTO | undefined = model.declaration.find((id) => id.applicantId == applicant.id);
                // End - declaration information

                // Start - Credit Report
                let creditReport: CreditReportDTO[] | undefined = model.creditReport.filter((ed) => ed.applicantId == applicant.id);
                // End - Credit Report

                // Start - Supporting Information
                // let arr: SupportingInformationDTO[] = [];
                // model.supportingInformation.forEach((item: SupportingInformationDTO, index: number) => {
                //     let simodel = new SupportingInformationDTO();
                //     simodel.fromDto(item);
                //     arr.push(simodel)
                // });
                let supportingInformation: SupportingInformationDTO[] | undefined = model.supportingInformation.filter((ed) => ed.applicantId == applicant.id);
                // End - Supporting Information

                // Start - Supporting Information
                let amendments: Amendment[] | undefined = model.amendments.filter((ed) => ed.applicantId == applicant.id);
                // End - Supporting Information

                localApplicantModel.fromDto({
                    applicant,
                    personalDetails,
                    personalDetailPreviousAddresses,
                    personalDetailFamilyAddresses,
                    employmentDetails,
                    employmentDetailsArmedForcesProof,
                    employmentDetailsAuditedAccount,
                    incomeDetails,
                    incomeDetailsMoneyGifter,
                    incomeDetailsOutstandingLoan,
                    incomeDetailsCreditCard,
                    incomeDetailsFinancialCommitment,
                    statementDetails,
                    propertyDetails,
                    propertyDetailsPerson,
                    currentPropertyDetails,
                    newPropertyDetails,
                    backgroundInformation,
                    declaration,
                    creditReport,
                    supportingInformation,
                    amendments,
                });
                this.applicantModels.push(localApplicantModel);
            });
        }
    };
    @MobX.action
    public toDto = (): UpsertApplicationFormDTO => {
        let application: ApplicationFormDTO = {
            id: this.id,
            createdByUserId: this.createdByUserId,
            statusId: this.statusId,
            createdDate: this.createdDate,
            lastSavedDate: this.lastSavedDate,
            submittedDate: this.submittedDate,
            isSubmitted: this.isSubmitted,
            isCompleted: this.isCompleted,
            rejectReasonId: this.rejectReasonId,
            isDeleted: this.isDeleted,
            rejectReasonDetail: this.rejectReasonDetail,
            requestedSupportingInfo: this.requestedSupportingInfo,
        };

        let model: UpsertApplicationFormDTO = {
            applicationForm: application,
        };

        return model;
    };
    @MobX.action
    public resetModel = (): void => {
        this.id = "";
        this.createdByUserId = "";
        this.statusId = "";
        this.lastSavedDate = null;
        this.createdDate = null;
        this.submittedDate = null;
        this.isSubmitted = false;
        this.isDeleted = false;
        this.isCompleted = false;
        this.rejectReasonId = null;
        this.applicantModels = [];
        this.rejectReasonDetail = null;
        this.requestedSupportingInfo = false;
    };
}
